import React, { useState, useEffect, useCallback } from 'react';
import { KeyRound, Layers3, Pencil, RefreshCw, ShieldCheck, ShieldPlus, Trash2, UserPlus, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { buildModulePermissions, getEnabledModuleLabels, getRoleLabel, ROLE_COLORS } from './staffAccessConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const createBlankForm = (roles = [], modules = []) => ({
  id: null,
  name: '',
  email: '',
  password: '',
  role: roles[1]?.key || roles[0]?.key || 'receptionist',
  module_permissions: buildModulePermissions(roles[1]?.key || roles[0]?.key || 'receptionist', {}, modules),
});

const StaffManagement = ({ user }) => {
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(createBlankForm());
  const [roleForm, setRoleForm] = useState({ label: '', description: '' });
  const [moduleForm, setModuleForm] = useState({ label: '', description: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [staffRes, credentialsRes, configRes] = await Promise.all([
        axios.get(`${API}/staff/list`, { withCredentials: true }),
        axios.get(`${API}/staff/login-credentials`, { withCredentials: true }),
        axios.get(`${API}/staff/config`, { withCredentials: true }),
      ]);
      const nextRoles = configRes.data.roles || [];
      const nextModules = configRes.data.modules || [];
      setStaff(staffRes.data);
      setCredentials(credentialsRes.data.credentials || []);
      setRoles(nextRoles);
      setModules(nextModules);
      setForm((prev) => prev.id ? { ...prev, module_permissions: buildModulePermissions(prev.role, prev.module_permissions, nextModules) } : createBlankForm(nextRoles, nextModules));
    } catch {
      toast.error('Failed to load staff');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const closeForm = () => {
    setShowForm(false);
    setEditing(false);
    setSaving(false);
    setForm(createBlankForm(roles, modules));
  };

  const openCreate = () => {
    setEditing(false);
    setForm(createBlankForm(roles, modules));
    setShowForm(true);
  };

  const openEdit = (member) => {
    setEditing(true);
    setForm({
      id: member.id,
      name: member.name || '',
      email: member.email || '',
      password: '',
      role: member.role || roles[0]?.key || 'receptionist',
      module_permissions: buildModulePermissions(member.role, member.module_permissions || {}, modules),
    });
    setShowForm(true);
  };

  const updateRole = (role) => {
    setForm((prev) => ({
      ...prev,
      role,
      module_permissions: buildModulePermissions(role, prev.module_permissions, modules),
    }));
  };

  const toggleModule = (moduleKey, checked) => {
    setForm((prev) => ({
      ...prev,
      module_permissions: {
        ...prev.module_permissions,
        [moduleKey]: Boolean(checked),
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      name: form.name,
      email: form.email,
      role: form.role,
      module_permissions: form.module_permissions,
    };
    if (form.password) payload.password = form.password;

    try {
      if (editing) {
        await axios.put(`${API}/staff/${form.id}`, payload, { withCredentials: true });
        toast.success('Staff access updated');
      } else {
        if (!form.password || form.password.length < 6) {
          toast.error('Password must be at least 6 characters');
          setSaving(false);
          return;
        }
        await axios.post(`${API}/staff/create`, payload, { withCredentials: true });
        toast.success('Staff account created');
      }
      closeForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to save staff');
      setSaving(false);
    }
  };

  const handleDelete = async (staffId, email) => {
    if (email === user.email) {
      toast.error('You cannot delete your own account');
      return;
    }
    if (!window.confirm(`Delete staff member ${email}?`)) return;
    try {
      await axios.delete(`${API}/staff/${staffId}`, { withCredentials: true });
      toast.success('Staff deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/staff/roles`, roleForm, { withCredentials: true });
      toast.success('New staff role added');
      setRoleForm({ label: '', description: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Role creation failed');
    }
  };

  const handleCreateModule = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/staff/modules`, moduleForm, { withCredentials: true });
      toast.success('New staff module added');
      setModuleForm({ label: '', description: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Module creation failed');
    }
  };

  const assignableModules = modules.filter((module) => module.assignable);

  return (
    <div className="space-y-5" data-testid="staff-management-module">
      <div className="flex flex-col gap-4 rounded-[1.75rem] bg-white p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-sky-600">Admin controls</p>
          <h2 className="text-3xl font-semibold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>Staff Management</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">Create roles, add modules, manage credentials, and assign permissions including Home Collection and Attendance.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline" size="icon" data-testid="refresh-staff-btn"><RefreshCw className="h-4 w-4" /></Button>
          <Button onClick={openCreate} className="bg-[#0A2540]" data-testid="add-staff-btn"><UserPlus className="mr-2 h-4 w-4" /> Add Staff</Button>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.4fr_0.6fr]">
        <div className="grid gap-3">
          {loading ? (
            <div className="rounded-[1.75rem] bg-white p-8 text-center text-[#94A3B8]">Loading staff accounts...</div>
          ) : staff.map((member) => {
            const enabledModules = getEnabledModuleLabels(member, modules);
            return (
              <div key={member.id || member.email} className="rounded-[1.75rem] bg-white p-5 shadow-sm" data-testid={`staff-card-${member.email}`}>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-lg font-semibold text-[#0A2540]">{member.name}</p>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${ROLE_COLORS[member.role] || 'bg-slate-100 text-slate-700'}`}>{getRoleLabel(member.role, roles)}</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-500">{member.email}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" className="rounded-xl" onClick={() => openEdit(member)} data-testid={`edit-staff-${member.email}`}><Pencil className="mr-2 h-4 w-4" /> Edit</Button>
                    {member.email !== user.email && <Button type="button" variant="outline" className="rounded-xl border-red-200 text-red-600 hover:bg-red-50" onClick={() => handleDelete(member.id, member.email)} data-testid={`delete-staff-${member.email}`}><Trash2 className="mr-2 h-4 w-4" /> Remove</Button>}
                  </div>
                </div>

                <div className="mt-4 rounded-2xl bg-slate-50 p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#0A2540]"><ShieldCheck className="h-4 w-4 text-sky-600" /> Module access</div>
                  {member.role === 'admin' ? (
                    <p className="text-sm text-slate-600">Full access to all modules and controls.</p>
                  ) : enabledModules.length ? (
                    <div className="flex flex-wrap gap-2">
                      {enabledModules.map((label) => <span key={label} className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-700 shadow-sm">{label}</span>)}
                    </div>
                  ) : <p className="text-sm text-slate-500">No modules enabled yet.</p>}
                </div>
              </div>
            );
          })}
        </div>

        <div className="space-y-4">
          <div className="rounded-[1.75rem] bg-[#0A2540] p-6 text-white shadow-sm" data-testid="staff-permissions-guide">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-300">Permission notes</p>
            <h3 className="mt-3 text-2xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>How access works</h3>
            <ul className="mt-5 space-y-3 text-sm text-slate-200">
              <li>• Admin can add custom roles and custom sidebar modules.</li>
              <li>• Attendance covers leave, permission, camp attendance, and relieving duty.</li>
              <li>• Home Collection can be assigned by admin or receptionist only.</li>
            </ul>
          </div>

          <div className="rounded-[1.75rem] bg-white p-6 shadow-sm" data-testid="staff-role-module-builder">
            <div className="grid gap-5">
              <form onSubmit={handleCreateRole} className="space-y-3" data-testid="staff-create-role-form">
                <div className="flex items-center gap-3"><ShieldPlus className="h-5 w-5 text-sky-600" /><h3 className="text-lg font-semibold text-[#0A2540]">Add staff role</h3></div>
                <div><Label>Role Name</Label><Input value={roleForm.label} onChange={(e) => setRoleForm((prev) => ({ ...prev, label: e.target.value }))} className="mt-2 h-11 rounded-xl" data-testid="new-role-label" /></div>
                <div><Label>Description</Label><Input value={roleForm.description} onChange={(e) => setRoleForm((prev) => ({ ...prev, description: e.target.value }))} className="mt-2 h-11 rounded-xl" data-testid="new-role-description" /></div>
                <Button type="submit" className="rounded-xl bg-[#0A2540]" data-testid="create-role-btn">Create Role</Button>
              </form>

              <form onSubmit={handleCreateModule} className="space-y-3 border-t border-slate-200 pt-5" data-testid="staff-create-module-form">
                <div className="flex items-center gap-3"><Layers3 className="h-5 w-5 text-sky-600" /><h3 className="text-lg font-semibold text-[#0A2540]">Add staff module</h3></div>
                <div><Label>Module Name</Label><Input value={moduleForm.label} onChange={(e) => setModuleForm((prev) => ({ ...prev, label: e.target.value }))} className="mt-2 h-11 rounded-xl" data-testid="new-module-label" /></div>
                <div><Label>Description</Label><Input value={moduleForm.description} onChange={(e) => setModuleForm((prev) => ({ ...prev, description: e.target.value }))} className="mt-2 h-11 rounded-xl" data-testid="new-module-description" /></div>
                <Button type="submit" className="rounded-xl bg-[#0A2540]" data-testid="create-module-btn">Create Module</Button>
              </form>
            </div>
          </div>

          <div className="rounded-[1.75rem] bg-white p-6 shadow-sm" data-testid="staff-internal-credentials-panel">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0A2540]/6"><KeyRound className="h-5 w-5 text-[#0A2540]" /></div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-600">Admin only</p>
                <h3 className="mt-1 text-2xl font-semibold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>Internal login credentials</h3>
                <p className="mt-2 text-sm text-slate-500">Seeded accounts stay hidden from the public login page and only appear here after admin login.</p>
              </div>
            </div>

            <div className="mt-5 space-y-3" data-testid="staff-internal-credentials-list">
              {credentials.map((credential) => (
                <div key={`${credential.role}-${credential.email}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid={`admin-credential-card-${credential.email.replace(/[@.]/g, '-')}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-[#0A2540]">{credential.label}</p>
                      <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-slate-500">{credential.role}</p>
                    </div>
                    <span className="rounded-full bg-sky-100 px-3 py-1 text-xs font-medium text-sky-700">Visible to admin</span>
                  </div>
                  <div className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
                    <div className="rounded-xl bg-white px-3 py-2"><span className="block text-[11px] uppercase tracking-[0.18em] text-slate-400">Username</span><span className="mt-1 block break-all font-medium text-slate-700">{credential.email}</span></div>
                    <div className="rounded-xl bg-white px-3 py-2"><span className="block text-[11px] uppercase tracking-[0.18em] text-slate-400">Password</span><span className="mt-1 block font-medium text-slate-700">{credential.password}</span></div>
                  </div>
                  <p className="mt-3 text-xs leading-5 text-slate-500">{credential.notes}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[1.75rem] bg-white p-6 shadow-sm" data-testid="staff-role-module-list-panel">
            <div className="flex items-center gap-3"><Users className="h-5 w-5 text-sky-600" /><h3 className="text-lg font-semibold text-[#0A2540]">Current roles and modules</h3></div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Roles</p>
                <div className="mt-2 flex flex-wrap gap-2">{roles.map((role) => <span key={role.key} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">{role.label}</span>)}</div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Modules</p>
                <div className="mt-2 flex flex-wrap gap-2">{modules.map((module) => <span key={module.key} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">{module.label}</span>)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showForm} onOpenChange={(open) => (!open ? closeForm() : setShowForm(true))}>
        <DialogContent className="max-w-3xl overflow-hidden rounded-[1.75rem] p-0" data-testid="staff-form-dialog">
          <div className="bg-gradient-to-r from-[#0A2540] to-[#0EA5E9] p-6 text-white">
            <DialogHeader>
              <DialogTitle className="text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>{editing ? 'Edit Staff Access' : 'Create Staff Account'}</DialogTitle>
              <DialogDescription className="text-slate-100/80">Configure role and exact modules for this staff account.</DialogDescription>
            </DialogHeader>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 p-6" data-testid="staff-form">
            <div className="grid gap-4 md:grid-cols-2">
              <div><Label>Full Name</Label><Input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} required className="mt-2 h-11 rounded-xl" data-testid="staff-name" /></div>
              <div><Label>Email / Username</Label><Input value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} required className="mt-2 h-11 rounded-xl" data-testid="staff-email" /></div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div><Label>{editing ? 'New Password (optional)' : 'Password'}</Label><Input type="password" value={form.password} onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))} minLength={editing ? 0 : 6} placeholder={editing ? 'Leave blank to keep current password' : 'Minimum 6 characters'} className="mt-2 h-11 rounded-xl" data-testid="staff-password" /></div>
              <div>
                <Label>Role</Label>
                <select className="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm" value={form.role} onChange={(e) => updateRole(e.target.value)} data-testid="staff-role">
                  {roles.map((role) => <option key={role.key} value={role.key}>{role.label}</option>)}
                </select>
              </div>
            </div>

            <div className="rounded-2xl bg-slate-50 p-4" data-testid="staff-module-permissions-section">
              <div className="mb-3"><p className="text-sm font-semibold text-[#0A2540]">Module permissions</p><p className="text-xs text-slate-500">Admin-added modules appear here automatically.</p></div>
              {form.role === 'admin' ? (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">Admins automatically receive full access across all current modules.</div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  {assignableModules.map((module) => (
                    <label key={module.key} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`permission-row-${module.key}`}>
                      <Checkbox checked={Boolean(form.module_permissions[module.key])} onCheckedChange={(checked) => toggleModule(module.key, checked)} data-testid={`permission-toggle-${module.key}`} />
                      <span>
                        <span className="block text-sm font-semibold text-[#0A2540]">{module.label}</span>
                        <span className="mt-1 block text-xs leading-5 text-slate-500">{module.description}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <Button type="button" variant="outline" onClick={closeForm} className="rounded-xl" data-testid="staff-cancel">Cancel</Button>
              <Button type="submit" className="rounded-xl bg-[#0A2540]" disabled={saving} data-testid="staff-submit">{saving ? 'Saving...' : editing ? 'Save Changes' : 'Create Staff Account'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffManagement;